import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  alpha,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import InstructionDocuments from '../instructions/InstructionDocuments';
import { instructionData } from '../instructions/instructionData';

import { useStore } from 'zustand';
import { useQueryClient } from 'react-query';
import { AssociatedEstimate, Contract } from '../../api/protosCompiled/contract/contract_pb';
import {
  useCreateContractMutation,
  useUpdateContractMutation,
} from '../../hooks/useCreateContractMutation';
import { defaultContract, useUnityBuildStore } from '../../stores/store';
import { useFetchContracts } from '../../hooks/useFetchContracts';
import { useFetchEstimates } from '../../hooks/useFetchProjectEstimates';
import { acceptedEstimateId } from '../../utils/useLockBid';
import { useUpdateSellingPriceMutation } from '../../hooks/useCloseoutHooks';

export const CreateContract: React.FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const isCreatingContract = useStore(useUnityBuildStore).isCreatingContract;
  const selectedContract = useStore(useUnityBuildStore).selectedContract;
  const setSelectedContract = useStore(useUnityBuildStore).setSelectedContract;
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const setIsCreatingContract = useStore(useUnityBuildStore).setIsCreatingContract;
  const setIsViewingContractForm = useStore(useUnityBuildStore).setIsViewingContractForm;

  const { data: estimateList } = useFetchEstimates();
  const { data: contracts } = useFetchContracts();
  const { mutate: updateSellingPrice } = useUpdateSellingPriceMutation();

  //log estimateList
  useEffect(() => {
    console.log('estimateList', estimateList);
  }, [estimateList]);

  const { mutate: createContract, isLoading: isCreating } = useCreateContractMutation();
  const { mutate: updateContract, isLoading: isUpdating } = useUpdateContractMutation();

  const isSubmitting = isCreating || isUpdating;

  const [customId, setCustomId] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [contractDescription, setContractDescription] = useState<string>('');
  const [associatedEstimates, setAssociatedEstimates] = useState<AssociatedEstimate.AsObject[]>([]);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState<string>('');
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    customId?: string;
    submit?: string;
  }>({});
  const [showLoadingDialog, setShowLoadingDialog] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');

  // State for instruction dialog
  const [showInstructionDialog, setShowInstructionDialog] = useState(false);
  const [instructionIndex, setInstructionIndex] = useState(0);

  // Function to open instruction dialog to "Contracts & Change Orders" section
  const openContractsInstructions = () => {
    const contractsIndex = instructionData.findIndex(
      item => item.title === 'Contracts & Change Orders'
    );
    setInstructionIndex(contractsIndex >= 0 ? contractsIndex : 0);
    setShowInstructionDialog(true);
  };

  useEffect(() => {
    if (!isCreatingContract) {
      if (!contracts || contracts.length === 0 || !selectedContract) {
        console.log('Contracts or selectedContract is not defined.');
        return;
      }

      // Normalize IDs to avoid any subtle comparison issues
      const selectedContractObj = contracts.find(
        contract => contract.contractid.trim() === selectedContract.contractid.trim()
      );

      if (!selectedContractObj) {
        setSelectedContract(defaultContract);
      } else {
        setCustomId(selectedContractObj.customid ?? '');
        setName(selectedContractObj.contractname ?? '');
        setContractDescription(selectedContractObj.contractdescription ?? '');
        setAssociatedEstimates(selectedContractObj?.associatedestimatesList ?? []);
      }
    }
    //eslint-disable-next-line
  }, [selectedContract, contracts]);

  type Field = 'customId' | 'contractName' | 'contractDescription' | 'associatedEstimates';

  const validateForm = () => {
    const errors: { name?: string; customId?: string } = {};

    if (!name.trim()) {
      errors.name = 'Contract Name is required';
    }

    if (!customId.trim()) {
      errors.customId = 'Contract ID is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSetValue = (field: Field, value: string) => {
    // Clear errors when user starts typing
    if (field === 'contractName' && formErrors.name) {
      setFormErrors(prev => ({ ...prev, name: undefined }));
    } else if (field === 'customId' && (formErrors.customId || formErrors.submit)) {
      setFormErrors(prev => ({ ...prev, customId: undefined, submit: undefined }));
    }

    switch (field) {
      case 'customId':
        setCustomId(value);
        break;
      case 'contractName':
        setName(value);
        break;
      case 'contractDescription':
        setContractDescription(value);
        break;
      default:
        break;
    }
  };

  const handleEstimateToggle = (estimateObj: any) => {
    const estimate = estimateObj.estimate;
    const isCurrentlySelected = associatedEstimates.some(
      associatedEstimate => associatedEstimate.estimateid === estimate.estimateid
    );

    if (isCurrentlySelected) {
      // Remove from selection
      setAssociatedEstimates(
        associatedEstimates.filter(
          associatedEstimate => associatedEstimate.estimateid !== estimate.estimateid
        )
      );
    } else {
      // Add to selection
      const newEstimate = {
        estimateid: estimate.estimateid,
        contractid: estimate.contractid,
        projectid: estimate.projectid ?? '',
        description: estimate.description ?? '',
        duedate: estimate.duedate ?? '',
        datecreated: estimate.datecreated ?? '',
        name: estimate.name ?? '',
        customid: estimate.customid ?? '',
        createdbyuserid: estimate.createdbyuserid ?? '',
        createdbyaccountid: estimate.createdbyaccountid ?? '',
        lastupdated: estimate.lastupdated ?? '',
        masterformatdivisionid: estimate.masterformatdivisionid ?? '',
        masterformatdivisionname: estimate.masterformatdivisionname ?? '',
        estimatebudget: estimate.estimatebudget ?? undefined,
        associatedchangeordersList: estimate.associatedchangeordersList ?? [],
        estimatestatusid: estimate.estimatestatusid ?? '',
        estimatestatusname: estimate.estimatestatusname ?? '',
      };
      setAssociatedEstimates([...associatedEstimates, newEstimate]);
    }
  };

  const formatAsCurrency = (value: number | undefined): string => {
    if (value === undefined) return '';
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handlePriceSubmit = (estimateId: string, value: string) => {
    if (!value) return;

    // Only allow numbers and decimal point
    const validPrice = value.replace(/[^0-9.]/g, '');
    const newPrice = parseFloat(validPrice);

    if (!isNaN(newPrice)) {
      console.log('Updating selling price for estimate:', estimateId, 'New price:', newPrice);

      // Optimistically update the cache
      const estimatesQueryKey = ['estimates', selectedProject.id];
      const previousData = queryClient.getQueryData(estimatesQueryKey);

      // Update the cache optimistically
      queryClient.setQueryData(estimatesQueryKey, (oldData: any) => {
        if (!oldData) return oldData;

        return oldData.map((estimateObj: any) => {
          if (estimateObj.estimate?.estimateid === estimateId) {
            return {
              ...estimateObj,
              estimate: {
                ...estimateObj.estimate,
                sellingprice: newPrice,
              },
            };
          }
          return estimateObj;
        });
      });

      // Call the mutation with optimistic update handling
      updateSellingPrice(
        { estimateid: estimateId, sellingprice: newPrice, sessiontoken: '' },
        {
          onError: () => {
            // Rollback on error
            queryClient.setQueryData(estimatesQueryKey, previousData);
          },
        }
      );
    }
    setEditingPriceId(null);
    setEditingPriceValue('');
  };

  const handlePriceInputChange = (value: string) => {
    // Allow numbers, one decimal point, and up to 2 decimal places
    const validInput = value.replace(/[^0-9.]/g, '');

    // Ensure only one decimal point
    const parts = validInput.split('.');
    let cleanValue = parts[0];
    if (parts.length > 1) {
      // Limit to 2 decimal places
      cleanValue += '.' + parts[1].slice(0, 2);
    }

    setEditingPriceValue(cleanValue);
  };

  const startEditing = (estimateId: string, currentPrice: number | undefined) => {
    setEditingPriceId(estimateId);
    setEditingPriceValue(currentPrice?.toString() || '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Clear any previous submit errors and show loading dialog
    setFormErrors(prev => ({ ...prev, submit: undefined }));

    // Set appropriate loading message and show dialog
    const operationType = isCreatingContract ? 'Creating' : 'Updating';
    setLoadingMessage(`${operationType} contract "${name}"...`);
    setShowLoadingDialog(true);

    const formData: Contract.AsObject = {
      contractid: selectedContract?.contractid ?? '',
      customid: customId,
      projectid: selectedProject.id,
      datecreated: selectedContract?.datecreated ?? '',
      lastupdated: '',
      contractname: name,
      contractdescription: contractDescription,
      associatedestimatesList: associatedEstimates,
    };

    try {
      // First, perform the mutation without the default onSuccess handlers
      if (isCreatingContract) {
        await new Promise((resolve, reject) => {
          createContract(formData, {
            onSuccess: resolve,
            onError: reject,
          });
        });
      } else {
        await new Promise((resolve, reject) => {
          updateContract(formData, {
            onSuccess: resolve,
            onError: reject,
          });
        });
      }

      // Update loading message to show we're refreshing the list
      setLoadingMessage('Refreshing contracts list...');

      // Now invalidate and wait for the contracts query to refetch
      await queryClient.invalidateQueries(['contracts', selectedProject.id]);

      // Wait for the contracts query to be in a success state
      await queryClient.refetchQueries(['contracts', selectedProject.id]);

      // Success - close form and reset
      resetForm();
      setIsViewingContractForm(false);
      setIsCreatingContract(false);
    } catch (error: any) {
      console.error('Error saving contract:', error);

      // Check for duplicate ID constraint violation
      if (error?.message?.includes('unique constraint "unique_custom_id_account_id"')) {
        setFormErrors(prev => ({
          ...prev,
          customId: 'This Contract ID is already in use. Please choose a different ID.',
        }));
      } else {
        // Generic error message for other errors
        setFormErrors(prev => ({
          ...prev,
          submit: 'An error occurred while saving the contract. Please try again.',
        }));
      }
    } finally {
      // Always hide loading dialog when operation completes
      setShowLoadingDialog(false);
      setLoadingMessage('');
    }
  };

  const handleContractFormCancel = () => {
    setIsCreatingContract(false);
    setIsViewingContractForm(false);
  };

  const resetForm = () => {
    setCustomId('');
    setName('');
    setContractDescription('');
    setFormErrors({});
  };

  const renderSellingPriceCell = (estimate: any, isSelected: boolean) => (
    <TableCell
      sx={{
        color: theme.palette.text.primary,
        padding: '8px',
      }}
    >
      <TextField
        size="small"
        value={
          editingPriceId === estimate.estimateid
            ? editingPriceValue
            : formatAsCurrency(estimate.sellingprice)
        }
        onFocus={() => startEditing(estimate.estimateid, estimate.sellingprice)}
        onChange={e => {
          if (editingPriceId === estimate.estimateid) {
            handlePriceInputChange(e.target.value);
          }
        }}
        onBlur={() => {
          if (editingPriceId === estimate.estimateid) {
            handlePriceSubmit(estimate.estimateid, editingPriceValue);
          }
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (editingPriceId === estimate.estimateid) {
              handlePriceSubmit(estimate.estimateid, editingPriceValue);
              const input = e.target as HTMLInputElement;
              input.blur();
            }
          }
        }}
        sx={{
          width: '120px',
          '& .MuiInputBase-input': {
            fontWeight: isSelected ? 600 : 400,
            textAlign: 'right',
            padding: '4px 8px',
          },
        }}
        inputProps={{
          style: {
            fontSize: '0.875rem',
          },
        }}
      />
    </TableCell>
  );

  return (
    <>
      {/* Loading Dialog */}
      <Dialog
        open={showLoadingDialog}
        disableEscapeKeyDown
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 1,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {isCreatingContract ? 'Creating Contract' : 'Updating Contract'}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 3,
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{
              color: theme.palette.primary.main,
            }}
          />
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            sx={{ maxWidth: 300 }}
          >
            {loadingMessage}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}
          >
            Please wait while we process your request...
          </Typography>
        </DialogContent>
      </Dialog>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ mt: 1 }}
        onKeyDown={e => {
          if (
            e.key === 'Enter' &&
            e.target instanceof HTMLInputElement &&
            e.target.type !== 'submit'
          ) {
            e.preventDefault();
          }
        }}
      >
        {/* Contract Guidance */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                📋 Creating a Contract
              </Typography>
              <Typography variant="body2">
                This form enables you to <strong>assign approved estimates</strong> to a contract
                and then
                <strong> create change orders</strong> for these estimates. Once estimates are
                associated with a contract, you can generate change orders to modify the original
                scope.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Only estimates with "Accepted" status can be assigned to contracts.
              </Typography>
            </Box>
            <Tooltip title="Learn more about Contracts & Change Orders">
              <IconButton
                onClick={openContractsInstructions}
                size="small"
                sx={{ ml: 1, color: 'info.main' }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Alert>

        <div style={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="contractName"
            label="Contract Name"
            rows={4}
            value={name}
            onChange={e => handleSetValue('contractName', e.target.value)}
            disabled={isSubmitting}
            error={!!formErrors.name}
            helperText={formErrors.name}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="customId"
            label="Contract ID"
            rows={4}
            value={customId}
            onChange={e => handleSetValue('customId', e.target.value)}
            disabled={isSubmitting}
            error={!!formErrors.customId}
            helperText={formErrors.customId}
          />
        </div>
        <TextField
          margin="normal"
          fullWidth
          multiline
          id="contractDescription"
          label="Contract Description"
          rows={4}
          value={contractDescription}
          onChange={e => handleSetValue('contractDescription', e.target.value)}
          disabled={isSubmitting}
        />

        {/* Table for selecting accepted estimates */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Associate Estimates (Accepted Only)
          </Typography>

          {/* Show selected estimates summary */}
          {associatedEstimates.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Selected: {associatedEstimates.map(est => est.name).join(', ')}
            </Typography>
          )}

          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[1],
              borderRadius: 1,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                    }}
                  >
                    Select
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                    }}
                  >
                    Estimate ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                    }}
                  >
                    Division
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                    }}
                  >
                    Estimate Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                    }}
                  >
                    Created Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      backgroundColor:
                        theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                      color: theme.palette.text.primary,
                    }}
                  >
                    Selling Price (Base Estimate)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {estimateList
                  ?.filter(estimateObj => {
                    // Only show estimates that have been accepted
                    if (estimateObj.estimate?.estimatestatusid !== acceptedEstimateId) {
                      return false;
                    }

                    const estimate = estimateObj.estimate;
                    if (!estimate) return false;

                    // Check if this estimate is already associated with any contract
                    const associatedContract = contracts?.find(contract =>
                      contract.associatedestimatesList?.some(
                        associatedEst => associatedEst.estimateid === estimate.estimateid
                      )
                    );

                    // If estimate is not associated with any contract, show it
                    if (!associatedContract) {
                      return true;
                    }

                    // If estimate is already associated with a contract:
                    // - When creating a new contract, don't show it
                    // - When editing an existing contract, only show it if it's associated with the contract being edited
                    if (isCreatingContract) {
                      return false;
                    } else {
                      return associatedContract.contractid === selectedContract?.contractid;
                    }
                  })
                  .map(estimateObj => {
                    const estimate = estimateObj.estimate;
                    const isSelected = associatedEstimates.some(
                      associatedEstimate => associatedEstimate.estimateid === estimate?.estimateid
                    );

                    return (
                      estimate && (
                        <TableRow
                          key={estimate.estimateid}
                          hover
                          onClick={() => !isSubmitting && handleEstimateToggle(estimateObj)}
                          sx={{
                            cursor: isSubmitting ? 'default' : 'pointer',
                            transition: 'background-color 0.2s',
                            backgroundColor: isSelected
                              ? alpha(
                                  theme.palette.primary.main,
                                  theme.palette.mode === 'dark' ? 0.15 : 0.08
                                )
                              : 'inherit',
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.1 : 0.05
                              ),
                            },
                            '&:nth-of-type(even)': {
                              backgroundColor: isSelected
                                ? alpha(
                                    theme.palette.primary.main,
                                    theme.palette.mode === 'dark' ? 0.15 : 0.08
                                  )
                                : theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.common.white, 0.02)
                                  : alpha(theme.palette.common.black, 0.02),
                            },
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ color: theme.palette.text.primary }}>
                            <Checkbox
                              checked={isSelected}
                              disabled={isSubmitting}
                              onChange={() => handleEstimateToggle(estimateObj)}
                              onClick={e => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            {estimate.customid || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            {estimate.masterformatdivision?.divisionname || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                              {estimate.name || 'Unnamed Estimate'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            {new Date(estimate.datecreated).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </TableCell>
                          {renderSellingPriceCell(estimate, isSelected)}
                        </TableRow>
                      )
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Show message when no estimates are available */}
          {estimateList?.filter(estimateObj => {
            // Only show estimates that have been accepted
            if (estimateObj.estimate?.estimatestatusid !== acceptedEstimateId) {
              return false;
            }

            const estimate = estimateObj.estimate;
            if (!estimate) return false;

            // Check if this estimate is already associated with any contract
            const associatedContract = contracts?.find(contract =>
              contract.associatedestimatesList?.some(
                associatedEst => associatedEst.estimateid === estimate.estimateid
              )
            );

            // If estimate is not associated with any contract, show it
            if (!associatedContract) {
              return true;
            }

            // If estimate is already associated with a contract:
            // - When creating a new contract, don't show it
            // - When editing an existing contract, only show it if it's associated with the contract being edited
            if (isCreatingContract) {
              return false;
            } else {
              return associatedContract.contractid === selectedContract?.contractid;
            }
          }).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {isCreatingContract
                ? 'No available estimates for contract association. All accepted estimates are already associated with other contracts.'
                : 'No accepted estimates available for this contract. All accepted estimates are already associated with other contracts.'}
            </Typography>
          )}
        </Box>

        {/* Display submit error */}
        {formErrors.submit && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formErrors.submit}
          </Alert>
        )}

        <Box sx={{ position: 'relative' }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
            disabled={isSubmitting}
          >
            {isCreatingContract ? 'Create Contract' : 'Update Contract'}
            {isSubmitting && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Button>
        </Box>
        <Button onClick={handleContractFormCancel} disabled={isSubmitting}>
          Cancel
        </Button>

        {/* Instruction Dialog */}
        <Dialog
          open={showInstructionDialog}
          onClose={() => setShowInstructionDialog(false)}
          maxWidth="md"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              height: '80vh',
            },
          }}
        >
          <DialogTitle
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="h6">Contracts & Change Orders Guide</Typography>
            <IconButton onClick={() => setShowInstructionDialog(false)} size="small">
              <InfoIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
            <InstructionDocuments
              subjects={instructionData}
              currentIndex={instructionIndex}
              onNavigate={setInstructionIndex}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </>
  );
};
